import { useCallback, useMemo } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Download, LayoutGrid, List, Plane } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { TravelSegmentDetail, KindIcon } from "@/components/travel/TravelSegmentDetail"
import { TravelGridView } from "@/components/travel/TravelGridView"
import { TravelForm, type TravelFormKind } from "@/components/travel/TravelForm"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { useRightRail } from "@/lib/right-rail"
import {
  buildSegments,
  eventTravelQueryOptions,
  formatDayLong,
  formatTime,
  travelersLabel,
  type TravelSegment,
} from "@/core/travel"

type TravelViewMode = "list" | "grid"

type TravelSearch = {
  /** Which column to fullscreen (1=list, 2=detail/form). */
  focus?: number
  /** list (table, default) | grid (day rows × type lanes). Persisted in the URL. */
  view?: TravelViewMode
  /** When set, col 2 shows the add-travel form for this kind. */
  add?: TravelFormKind
  /** When set with a $segmentId, col 2 shows the edit form. */
  edit?: boolean
  /** Legacy — redirected into the /travel/$segmentId path by beforeLoad. */
  segmentId?: string
}

const ADD_KINDS: TravelFormKind[] = ["flight", "ground", "rental"]

const TRAVEL_DISPLAY_MODES = [
  { id: "list", label: "List", icon: <List className="h-3 w-3" /> },
  { id: "grid", label: "Grid", icon: <LayoutGrid className="h-3 w-3" /> },
]

// The Travel LAYOUT route. Route DEPTH = Main columns (mirrors forms.tsx):
//   /travel              → col 1 (list/grid)
//   /travel/$segmentId   → col 1 + 2 (+ segment detail / edit form)
//   /travel?add=flight   → col 1 + 2 (+ add form, no segment yet)
// `$segmentId` lives on a MARKER child route; ColumnLayout decides how many fit.
// `?focus=2` fullscreens col 2. Legacy `?segmentId=…` redirects into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/travel")({
  validateSearch: (s: Record<string, unknown>): TravelSearch => {
    return {
      focus: parseRouteFocus(s.focus),
      view: s.view === "grid" ? "grid" : s.view === "list" ? "list" : undefined,
      add: ADD_KINDS.includes(s.add as TravelFormKind) ? (s.add as TravelFormKind) : undefined,
      edit: s.edit === true || s.edit === "true" ? true : undefined,
      segmentId: typeof s.segmentId === "string" ? s.segmentId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.segmentId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/travel/$segmentId",
        params: { ...params, segmentId: search.segmentId },
        search: { focus: search.focus, view: search.view, edit: search.edit },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(eventTravelQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: TravelView,
})

const TYPE_BADGE: Record<TravelSegment["kind"], string> = {
  flight: "text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-500/15",
  transfer: "text-violet-700 dark:text-violet-400 bg-violet-100 dark:bg-violet-500/15",
  ground: "text-plum-700/70 dark:text-plum-200/55 bg-plum-100 dark:bg-white/10",
  rental: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
}

const STATUS_PILL: Record<TravelSegment["status"], { label: string; cls: string }> = {
  scheduled: {
    label: "Scheduled",
    cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  },
  completed: {
    label: "Completed",
    cls: "bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45",
  },
  unscheduled: {
    label: "Unscheduled",
    cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  },
}

function TravelView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/travel" })
  const { focus, view: viewParam, add: addKind, edit } = Route.useSearch()
  const view: TravelViewMode = viewParam ?? "list"
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const showInRail = useRightRail((s) => s.show)

  const { segmentId: selectedId } = useRouteRecordSelection("segmentId")

  const setView = useCallback(
    (next: TravelViewMode) =>
      // Switching view returns to the list (clears any open detail/form).
      void navigate({ to: "/$orgSlug/$eventSlug/travel", params, search: { view: next } }),
    [navigate, params],
  )
  const openSegment = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/travel/$segmentId",
        params: { ...params, segmentId: id },
        search: { view },
      }),
    [navigate, params, view],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/travel", params, search: { view } }),
    [navigate, params, view],
  )
  const openAdd = useCallback(
    (kind: TravelFormKind = "flight") =>
      void navigate({ to: "/$orgSlug/$eventSlug/travel", params, search: { view, add: kind } }),
    [navigate, params, view],
  )
  const openEdit = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/travel/$segmentId",
        params: { ...params, segmentId: id },
        search: { view, edit: true },
      }),
    [navigate, params, view],
  )

  const travelQ = useQuery({ ...eventTravelQueryOptions(eventId ?? ""), enabled: !!eventId })
  const segments = useMemo(() => buildSegments(travelQ.data), [travelQ.data])
  const selected = useMemo(
    () => (selectedId ? segments.find((s) => s.id === selectedId) ?? null : null),
    [segments, selectedId],
  )

  const config = useMemo<CollectionConfig<TravelSegment>>(() => {
    const exportCsv = (rows: TravelSegment[]) => {
      downloadMappedCsv(
        "travel.csv",
        ["Type", "Route", "Traveler", "Operator", "Date", "Time", "Status", "Confirmation"],
        rows,
        (s) => [
          s.typeLabel,
          s.routeLabel,
          travelersLabel(s.passengers),
          s.operator ?? "",
          s.dayKey ?? "",
          s.startsAt ? formatTime(s.startsAt) : "",
          s.status,
          s.confirmation ?? "",
        ],
      )
    }

    return {
      title: "Travel",
      getId: (s) => s.id,
      onNew: () => openAdd("flight"),
      newLabel: "Add travel",
      searchText: (s) =>
        `${s.routeLabel} ${s.typeLabel} ${s.operator ?? ""} ${s.confirmation ?? ""} ${s.passengers
          .map((p) => p.name)
          .join(" ")}`,
      onOpen: (s) => openSegment(s.id),
      defaultSortId: "when",
      defaultSortDir: "asc",
      emptyTitle: "No travel movements",
      emptyBlurb: "Flights, ground transfers, and rentals appear here as travel is booked.",
      emptyIcon: <Plane className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "flights", label: "Flights", filter: (s) => s.tab === "flights" },
        {
          id: "ground",
          label: "Ground",
          filter: (s) => s.tab === "ground" && s.typeLabel !== "Point-to-point",
        },
        {
          id: "p2p",
          label: "Point-to-point",
          filter: (s) => s.typeLabel === "Point-to-point",
        },
        { id: "rentals", label: "Rentals", filter: (s) => s.tab === "rentals" },
      ],
      filters: [
        {
          id: "status",
          label: "Status",
          options: [
            ["all", "All"],
            ["scheduled", "Scheduled"],
            ["completed", "Completed"],
            ["unscheduled", "Unscheduled"],
          ],
          predicate: (v, s) => s.status === v,
        },
        {
          id: "direction",
          label: "Direction",
          options: [
            ["all", "All"],
            ["arriving", "Arriving"],
            ["departing", "Departing"],
          ],
          predicate: (v, s) => s.direction === v,
        },
      ],
      groupOptions: [
        {
          id: "day",
          label: "Day",
          groupOf: (s) => (s.dayKey ? formatDayLong(s.dayKey) : "Unscheduled"),
          order: (l) => (l === "Unscheduled" ? 99 : 0),
        },
        {
          id: "type",
          label: "Type",
          groupOf: (s) => s.typeLabel,
        },
      ],
      menuActions: [
        {
          id: "export-all",
          label: "Export all (CSV)",
          icon: <Download className="mb-mii" />,
          run: (rows) => exportCsv(rows),
        },
      ],
      columns: [
        {
          id: "route",
          header: "Movement",
          sortable: true,
          sortValue: (s) => s.routeLabel.toLowerCase(),
          render: (s) => (
            <span className="flex items-center gap-2">
              <KindIcon
                segment={s}
                className="h-3.5 w-3.5 shrink-0 text-plum-700/55 dark:text-plum-200/45"
              />
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {s.routeLabel}
              </span>
            </span>
          ),
        },
        {
          id: "type",
          header: "Type",
          render: (s) => (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ${TYPE_BADGE[s.kind]}`}
            >
              {s.typeLabel}
            </span>
          ),
        },
        {
          id: "traveler",
          header: "Traveler",
          className: "text-plum-700/75 dark:text-plum-200/60",
          render: (s) => {
            const first = s.passengers[0]
            if (!first) return <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  showInRail({ kind: "guest", guestId: first.id, name: first.name })
                }}
                className="rounded text-left hover:text-plum-900 hover:underline dark:hover:text-plum-50"
              >
                {travelersLabel(s.passengers)}
              </button>
            )
          },
        },
        {
          id: "operator",
          header: "Operator",
          className: "text-plum-700/65 dark:text-plum-200/50",
          render: (s) => s.operator ?? "—",
        },
        {
          id: "when",
          header: "When",
          className: "text-plum-700/60 dark:text-plum-200/45 tabular-nums",
          sortable: true,
          sortValue: (s) => s.startsAt ?? "9999",
          render: (s) =>
            s.startsAt ? (
              <span>
                <span className="text-plum-800/80 dark:text-plum-100/70">
                  {formatDayLong(s.dayKey ?? "")}
                </span>{" "}
                · {formatTime(s.startsAt)}
              </span>
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">Unscheduled</span>
            ),
        },
        {
          id: "status",
          header: "Status",
          render: (s) => {
            const p = STATUS_PILL[s.status]
            return (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.cls}`}>
                {p.label}
              </span>
            )
          },
        },
      ],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSegment, showInRail, openAdd])

  const showAdd = !!addKind
  const showEdit = !!selected && edit === true
  const activeCardId = selected ? selected.id : null
  const loadError = travelQ.isError
    ? ((travelQ.error as Error)?.message ?? "Failed to load travel")
    : travelQ.data && !travelQ.data.success
      ? "Couldn't load travel."
      : null
  const grid = (
    <TravelGridView
      segments={segments}
      data={travelQ.data}
      activeId={activeCardId}
      onOpen={openSegment}
      loading={travelQ.isLoading && segments.length === 0}
      error={loadError}
    />
  )
  // List/Grid lives in the toolbar's View segment (a display mode); the grid
  // renders through renderDisplay so the standardized header stays put.
  const table = (
    <CollectionView
      config={config}
      rows={segments}
      activeId={activeCardId}
      loading={travelQ.isLoading && segments.length === 0}
      error={loadError}
      displayModes={TRAVEL_DISPLAY_MODES}
      displayMode={view}
      onDisplayMode={(m) => setView(m as TravelViewMode)}
      renderDisplay={(m) => (m === "grid" ? grid : null)}
    />
  )

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [{ id: "list", minWidth: 320, width: 420, node: table }]
    // Col 2 = add form (no segment yet) | edit form | segment detail.
    if (showAdd && eventId) {
      cols.push({
        id: "detail",
        minWidth: 460,
        fill: true,
        node: (
          <TravelForm eventId={eventId} initialKind={addKind} onClose={closePane} onSaved={closePane} />
        ),
      })
    } else if (selected && eventId) {
      cols.push({
        id: "detail",
        minWidth: 460,
        fill: true,
        node: showEdit ? (
          <TravelForm
            eventId={eventId}
            segment={selected}
            onClose={() => openSegment(selected.id)}
            onSaved={() => openSegment(selected.id)}
          />
        ) : (
          <TravelSegmentDetail
            segment={selected}
            onClose={closePane}
            onEdit={selected.isStandalone ? undefined : () => openEdit(selected.id)}
            closeVariant="close"
          />
        ),
      })
    }
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, showAdd, showEdit, selected, addKind, eventId, closePane, openSegment, openEdit])

  return <RoutedCollectionLayout columns={columns} focus={focus} />
}
